/**
 * PoolStatusPill — admin-only NodePool health indicator.
 *
 * Polls /api/admin/cml/pool/status every 30 s. Renders a tiny pill in the
 * navbar showing per-topology capacity: "Pool 3/3 SR · 3/3 TR".
 *
 * Color states:
 *  - cyan   = at capacity (healthy)
 *  - amber  = below capacity (some slots warming or in cleanup)
 *  - red    = empty or any dead/error slots
 *  - gray   = pool not configured (no CML credentials)
 *
 * Designed so students NEVER see this — caller must gate on user.email ===
 * admin@ciscolab.ai before rendering. Round 35 ops visibility tool.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Server } from "lucide-react";
import { api } from "../lib/api";

const POLL_MS = 30_000;

export default function PoolStatusPill() {
  const [status, setStatus] = useState(null); // { configured, summary }
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/cml/pool/status");
        if (!cancelled) {
          setStatus(data);
          setErr(false);
        }
      } catch {
        if (!cancelled) setErr(true);
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (err) {
    return (
      <span
        className="hidden lg:flex items-center gap-1.5 px-2 py-1 border border-red-500/40 text-red-300 text-[10px] font-mono uppercase tracking-[0.18em]"
        data-testid="pool-status-pill-error"
        title="Pool status unreachable"
      >
        <Server size={11} /> pool n/a
      </span>
    );
  }

  if (!status) {
    return (
      <span
        className="hidden lg:flex items-center gap-1.5 px-2 py-1 border border-zinc-700 text-zinc-500 text-[10px] font-mono uppercase tracking-[0.18em]"
        data-testid="pool-status-pill-loading"
      >
        <Server size={11} /> pool ...
      </span>
    );
  }

  if (!status.configured) {
    return (
      <span
        className="hidden lg:flex items-center gap-1.5 px-2 py-1 border border-zinc-700 text-zinc-500 text-[10px] font-mono uppercase tracking-[0.18em]"
        data-testid="pool-status-pill-disabled"
        title="NodePool not initialised (no CML client?)"
      >
        <Server size={11} /> pool off
      </span>
    );
  }

  const cap = status.capacity || {};
  const avail = status.summary?.available || {};
  const dead = status.summary?.dead || 0;

  // Round 44: pools are keyed by topology type (IOL routers / IOL-L2 switches).
  const LABELS = {
    single_router: "R", two_routers: "2R", five_routers: "5R",
    single_switch: "SW", two_switches: "2SW", three_switches: "3SW", router_switch: "R+SW",
  };
  const types = Object.keys(LABELS).filter((t) => (cap[t] || 0) > 0);
  const totalAvail = types.reduce((n, t) => n + Math.min(avail[t] || 0, cap[t] || 0), 0);
  const totalCap = types.reduce((n, t) => n + (cap[t] || 0), 0);

  const allHealthy = types.every((t) => (avail[t] || 0) >= (cap[t] || 0)) && dead === 0;
  const anyEmpty = types.some((t) => (cap[t] || 0) > 0 && (avail[t] || 0) === 0);
  const hasDead = dead > 0;

  let tone;
  if (hasDead || anyEmpty) {
    tone = "border-red-500/60 text-red-300 bg-red-500/10";
  } else if (allHealthy) {
    tone = "border-[#00E5FF]/50 text-[#00E5FF] bg-[#00E5FF]/10";
  } else {
    tone = "border-amber-500/50 text-amber-300 bg-amber-500/10";
  }

  const offTypes = Object.keys(LABELS).filter((t) => !(cap[t] || 0));
  const tip = [
    ...types.map((t) => `${t.replace(/_/g, " ")} pool: ${avail[t] || 0}/${cap[t] || 0} available`),
    offTypes.length ? `OFF (capacity 0 — set NODE_POOL_CAP_<TYPE>): ${offTypes.map((t) => LABELS[t]).join(", ")}` : null,
    `Claimed: ${status.summary?.claimed || 0}`,
    `Cleaning: ${status.summary?.cleaning || 0}`,
    dead ? `⚠ DEAD slots: ${dead}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  const perType = types.slice(0, 4).map((t) => `${Math.min(avail[t] || 0, cap[t] || 0)}${LABELS[t].toLowerCase()}`).join(" ");

  return (
    <Link
      to="/admin/labs"
      className={`hidden lg:flex items-center gap-1.5 px-2 py-1 border ${tone} text-[10px] font-mono uppercase tracking-[0.18em] hover:opacity-80 transition-opacity`}
      data-testid="pool-status-pill"
      title={tip}
    >
      <Server size={11} />
      pool {totalAvail}/{totalCap}{perType ? ` · ${perType}` : ""}
      {hasDead && <span className="text-red-400 font-bold" data-testid="pool-status-dead-flag">·{dead}†</span>}
    </Link>
  );
}
